import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Volume2, VolumeX, Package, PackageCheck, Truck, Clock, Phone, MapPin } from "lucide-react";
import { api, peso, formatApiError } from "../../lib/api";
import { useOrderAlerts } from "../../hooks/useOrderAlerts";
import { Button } from "../../components/ui/button";

const STATUS_FLOW = {
  pending:          { next: "packing",           label: "Start packing",   color: "bg-[#E74C3C]", title: "NEW" },
  paid:             { next: "packing",           label: "Start packing",   color: "bg-[#E74C3C]", title: "NEW (paid)" },
  preparing:        { next: "packing",           label: "Start packing",   color: "bg-[#E74C3C]", title: "NEW" },
  packing:          { next: "ready",             label: "Mark as ready",   color: "bg-[#E67E22]", title: "PACKING" },
  ready:            { next: null,                label: "Awaiting driver", color: "bg-[#1565C0]", title: "READY" },
  assigned:         { next: "out_for_delivery",  label: "Out for delivery", color: "bg-[#1565C0]", title: "ASSIGNED" },
  out_for_delivery: { next: "delivered",         label: "Mark delivered",   color: "bg-[#2F5233]", title: "ON THE WAY" },
};

const MINUTES_WARN = 5;
const MINUTES_CRIT = 10;

function minutesSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

function ageColor(mins) {
  if (mins >= MINUTES_CRIT) return "border-[#C62828] bg-[#FDECEA]";
  if (mins >= MINUTES_WARN) return "border-[#E67E22] bg-[#FDF4E8]";
  return "border-[#2F5233] bg-white";
}

function OrderTile({ o, onStep }) {
  const mins = minutesSince(o.created_at);
  const flow = STATUS_FLOW[o.status] || { next: null, label: "—", color: "bg-gray-500", title: o.status };
  return (
    <div className={`rounded-2xl border-2 ${ageColor(mins)} shadow-sm p-4 flex flex-col gap-3`} data-testid={`order-tile-${o.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider text-white ${flow.color}`}>
            {flow.title}
          </div>
          <div className="font-heading text-xl font-extrabold text-[#1C2826] mt-1">#{o.number}</div>
        </div>
        <div className="text-right">
          <div className="font-heading text-lg font-extrabold text-[#2F5233]">{peso(o.total)}</div>
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#1C2826] mt-0.5">
            <Clock className="h-3.5 w-3.5" />
            {mins}m
          </div>
        </div>
      </div>

      <div className="text-sm space-y-1">
        <div className="flex items-start gap-1 text-[#1C2826] font-semibold">
          <span className="truncate">{o.customer_name || "Customer"}</span>
        </div>
        {o.phone && (
          <a href={`tel:${o.phone}`} className="flex items-center gap-1 text-[#2F5233] hover:underline text-xs">
            <Phone className="h-3 w-3" /> {o.phone}
          </a>
        )}
        {o.address && (
          <div className="flex items-start gap-1 text-[#4A5D5A] text-xs line-clamp-2">
            <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" /> <span>{o.address}</span>
          </div>
        )}
      </div>

      <ul className="text-sm space-y-0.5 border-t border-[#E1E5DF] pt-2 max-h-40 overflow-y-auto">
        {(o.items || []).map((i, idx) => (
          <li key={`${i.product_id || i.name}-${i.variant_id || ""}-${idx}`} className="flex items-start justify-between gap-2">
            <span className="font-semibold text-[#1C2826]">{i.quantity}× <span className="font-normal">{i.name}</span></span>
            <span className="text-[#738380] text-xs flex-shrink-0">{peso(i.price * i.quantity)}</span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between gap-2 mt-auto pt-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#738380]">
          {o.payment_method || "cod"}
        </span>
        {flow.next ? (
          <Button
            onClick={() => onStep(o, flow.next)}
            className="h-9 bg-[#2F5233] hover:bg-[#1F3D24] text-white text-xs"
            data-testid={`order-step-${o.id}`}
          >
            {o.status === "packing" ? <PackageCheck className="h-4 w-4 mr-1" /> : o.status === "assigned" ? <Truck className="h-4 w-4 mr-1" /> : <Package className="h-4 w-4 mr-1" />}
            {flow.label}
          </Button>
        ) : (
          <span className="text-xs text-[#1565C0] font-semibold italic">{flow.label}</span>
        )}
      </div>
    </div>
  );
}

export default function OrdersDisplay() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [now, setNow] = useState(Date.now());

  // Tick every 30 s to refresh minute-age
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  useOrderAlerts({
    enabled: soundOn,
    onData: setOrders,
  });

  // Also keep a poll even if sound is off
  useEffect(() => {
    if (soundOn) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/orders/live");
        if (!cancelled) setOrders(data);
      } catch (_) { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [soundOn]);

  const step = useCallback(async (order, newStatus) => {
    try {
      await api.post(`/orders/${order.id}/status`, { status: newStatus });
      toast.success(`#${order.number} → ${newStatus.replace(/_/g, " ")}`);
      const { data } = await api.get("/orders/live");
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const groups = useMemo(() => {
    const g = { new: [], packing: [], ready: [], out: [] };
    for (const o of orders) {
      if (["pending", "paid", "preparing"].includes(o.status)) g.new.push(o);
      else if (o.status === "packing") g.packing.push(o);
      else if (o.status === "ready") g.ready.push(o);
      else g.out.push(o); // assigned / out_for_delivery
    }
    return g;
  }, [orders, now]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#F1F3EE]">
      <div className="bg-[#1C2826] text-white px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/admin")} className="text-white hover:bg-white/10" data-testid="back-to-admin">
            <ArrowLeft className="h-4 w-4 mr-1" /> Admin
          </Button>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3C97A]">Orders Display</div>
            <div className="font-heading text-lg sm:text-xl font-extrabold tracking-tight">Arbe Minimart · Packing Station</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundOn((v) => !v)}
            className="text-white hover:bg-white/10"
            data-testid="toggle-sound"
            title={soundOn ? "Mute alerts" : "Unmute alerts"}
          >
            {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5 text-[#E74C3C]" />}
          </Button>
        </div>
      </div>

      <div className="px-3 sm:px-5 py-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Column title="NEW" color="bg-[#E74C3C]" orders={groups.new} onStep={step} empty="No new orders" />
        <Column title="PACKING" color="bg-[#E67E22]" orders={groups.packing} onStep={step} empty="Nothing being packed" />
        <Column title="READY · Awaiting driver" color="bg-[#1565C0]" orders={groups.ready} onStep={step} empty="No orders ready" />
        <Column title="OUT / ASSIGNED" color="bg-[#2F5233]" orders={groups.out} onStep={step} empty="No active deliveries" />
      </div>
    </div>
  );
}

function Column({ title, color, orders, onStep, empty }) {
  return (
    <div>
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${color} text-white mb-3`}>
        <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
        <span className="text-xs font-bold bg-white/20 rounded-full px-2 py-0.5">{orders.length}</span>
      </div>
      <div className="space-y-3">
        {orders.length === 0 && (
          <div className="text-center text-sm text-[#738380] py-8">{empty}</div>
        )}
        {orders.map((o) => <OrderTile key={o.id} o={o} onStep={onStep} />)}
      </div>
    </div>
  );
}
